<script lang="ts">
  // One card on the duel mat (or an empty zone slot when `card` is null). Renders real
  // CDN art keyed by the Konami passcode, with a styled text-frame fallback for passcodes
  // the CDN lacks, a card-back for hidden/face-down cards, defense-position rotation, an
  // Xyz-material count, and a selectable glow. Purely presentational, clicks/hover are
  // forwarded to the parent (Duel screen), which owns the duel logic.
  import type { CardInfo } from "@ygoevo/yo2-data";
  import type { QueriedCard } from "@ygoevo/protocol";
  import { POS, LOCATION } from "@ygoevo/protocol";
  import { mrefKey, takeRevealKey, type CardActionKind } from "../../stores/duel.svelte.ts";
  import { cardImage } from "../../../data.ts";
  import { frameColor } from "../../cards.ts";
  import { foilGradeClass } from "../../rarity.ts";
  import { sleeveById, DEFAULT_SLEEVE } from "../../duel/sleeves.ts";

  // The default classic card back (real art) shown for every face-down card on the 2D mat.
  const backImg = sleeveById(DEFAULT_SLEEVE).img;

  let {
    card,
    info = null,
    action = null,
    foil = 0,
    flip180 = false,
    active = false,
    picked = false,
    linked = false,
    label = "",
    onclick,
    onhover,
  }: {
    card: QueriedCard | null;
    /** Metadata for this card's code, if known (name + fallback frame). */
    info?: CardInfo | null;
    /** Best foil grade (1..3) you own of this card; 0 = plain. Only your own
     *  face-up cards foil (opponent cosmetics aren't sent). */
    foil?: number;
    /** The play this card currently offers (drives the coloured border + badge). null = no play. */
    action?: CardActionKind | null;
    /** Rotate the card 180°, the opponent's field reads from their side of the mat. */
    flip180?: boolean;
    /** Currently focused/selected, stronger highlight. */
    active?: boolean;
    /** Confirmed member of the current multi-card pick. Distinct from `active`, which is only focus. */
    picked?: boolean;
    /** Part of the hovered equip/target relationship group, show a chain-link badge + gold halo. */
    linked?: boolean;
    /** Tiny zone label shown when the slot is empty (e.g. "M", "S/T", "FIELD"). */
    label?: string;
    /** Click handler, receives the pointer event (for action-menu placement); absent on keyboard. */
    onclick?: (e?: MouseEvent) => void;
    onhover?: (hovering: boolean) => void;
  } = $props();

  // Has a play right now → interactive (pointer + keyboard). Like Master Duel / EDOPro, EVERY actionable
  // card wears the SAME single "you can act here" highlight (a warm gold glow), the specific play
  // (Summon / Activate / Attack / select…) is shown by the tap menu, not encoded in the border colour.
  const selectable = $derived(action != null);
  // A known face-up card is also inspectable even when it has no legal move. That is core duel
  // ergonomics (especially for an opponent's board), so it gets the same keyboard affordance as an
  // actionable card instead of being mouse-only.
  const inspectable = $derived(!!card && card.code !== 0);
  // A masked face-down card still has to receive input when the core offers it as a target. Gating
  // the handler on `inspectable` alone made set Defense Position monsters glow as legal attack
  // targets while remaining impossible to click because their public code is deliberately zero.
  const interactive = $derived(inspectable || selectable);
  const accessibleName = $derived.by(() => {
    if (!interactive) return undefined;
    const name = inspectable ? (info?.name ?? `card ${card?.code}`) : "face-down card";
    if (action === "target") return `${picked ? "Deselect" : "Select"} ${name}`;
    return `Inspect ${name}`;
  });
  // Pro Mode's neutral "pro" kind: clickable (the type menu opens) but NO legality affordance — the
  // halo/edge/z-raise are all gated on `hinted`, so a pro-mode card is indistinguishable from an
  // inert one until it's clicked. Withholding that knowledge is the mode's entire point.
  const hinted = $derived(selectable && action !== "pro");
  // Orange means "a play is available", magenta brackets mean "legal target", and lime means
  // "confirmed in the current pick". The explicit state ladder removes the old orange-on-orange
  // ambiguity without turning every different action type into a separate colour code.
  const accent = $derived(picked ? "var(--color-lime)" : action === "target" ? "var(--color-magenta)" : "#ff9322");

  let broken = $state(false);
  $effect(() => {
    void card?.code; // reset the broken flag when the card in this slot changes
    broken = false;
  });

  // Only cards ON THE FIELD carry a meaningful position. Hand cards report a face-down
  // position bit in the core query, so a card is a "back" when its code is masked from us
  // (0 = opponent's hidden card) OR it's a face-down card sitting in a monster/spell zone.
  // Our own set cards still arrive with a known code (the server only masks the opponent),
  // so the hover preview can reveal them while the mat shows a back, like a real game.
  const onField = $derived(!!card && (card.location === LOCATION.MZONE || card.location === LOCATION.SZONE));
  const masked = $derived(!!card && card.code === 0);
  const faceDown = $derived(masked || (onField && !!card && (card.position & POS.FACEDOWN) !== 0));

  // ── the REAL card flip ──────────────────────────────────────────────────────────
  // The rendered face lags the data: when `faceDown` changes for the same physical card (a Set trap
  // activating, Book of Moon, a flip summon), the card TURNS OVER — a perspective rotateY with the
  // face swapped at 90° — instead of the art snapping. A card that lands here already face-up but
  // whose flight flew as a masked BACK (opponent's hand play) flips in the same way on arrival.
  let box = $state<HTMLDivElement | null>(null);
  // svelte-ignore state_referenced_locally -- the initial value IS the point; the $effect below keeps it in sync
  let shownFaceDown = $state(faceDown);
  let flipping = false;
  let lastCode = -1;
  const reduced = () => typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  async function flipTo(fd: boolean): Promise<void> {
    const el = box;
    if (flipping || !el || typeof el.animate !== "function" || reduced()) { shownFaceDown = fd; return; }
    flipping = true;
    try {
      await el.animate(
        [{ transform: "perspective(520px) rotateY(0deg)" }, { transform: "perspective(520px) rotateY(90deg)" }],
        { duration: 150, easing: "cubic-bezier(0.64, 0, 0.78, 0)" }, // easeInQuint — the card lifts away
      ).finished;
      shownFaceDown = fd;
      await el.animate(
        [{ transform: "perspective(520px) rotateY(-90deg)" }, { transform: "perspective(520px) rotateY(0deg)" }],
        { duration: 190, easing: "cubic-bezier(0.22, 1, 0.36, 1)" }, // easeOutQuint — and settles
      ).finished;
    } catch { shownFaceDown = fd; }
    flipping = false;
    if (shownFaceDown !== faceDown) shownFaceDown = faceDown; // target moved mid-flip → settle on truth
  }
  $effect(() => {
    const c = card;
    if (!c) { lastCode = -1; return; }
    const fd = faceDown;
    const fresh = lastCode === -1;
    // masked (0) → real code is the SAME physical card being revealed; any other code change means the
    // slot's occupant was replaced — no flip theatre between two different cards.
    const sameCard = lastCode === c.code || lastCode === 0;
    lastCode = c.code;
    if (fresh) {
      // a card just landed in this slot: appear as-is — unless it arrived as a masked back that the
      // events have since named face-up, then it flips in from its back (the reveal, animated).
      if (!fd && onField && takeRevealKey(mrefKey(c.controller, c.location, c.sequence))) {
        shownFaceDown = true;
        void flipTo(false);
      } else shownFaceDown = fd;
      return;
    }
    if (fd === shownFaceDown) return;
    if (!sameCard) { shownFaceDown = fd; return; }
    void flipTo(fd);
  });
  const defense = $derived(!!card && card.location === LOCATION.MZONE && (card.position & POS.DEFENSE) !== 0);
  // Total on-mat rotation: opponent cards sit 180° (face their side), defense monsters turn 90° more.
  const rot = $derived((flip180 ? 180 : 0) + (defense ? -90 : 0));
  // ATK/DEF belong to MONSTERS only. The field query returns 0/0 for spell/traps too, so key off the
  // card TYPE bit (0x1 = monster), not `attack !== undefined`, otherwise a spell/trap shows a fake 0/0.
  const isMonster = $derived(!!card && card.type !== undefined && (card.type & 0x1) !== 0);
  // Total counters on the card (Spell Counters, A-Counters, …), shown as a gold pip badge.
  const counterTotal = $derived(card?.counters ? Object.values(card.counters).reduce((a, b) => a + b, 0) : 0);
  const fmt = (v: number | undefined | null) => (v === undefined || v === null ? "" : v === -2 ? "?" : String(v));
</script>

<!-- svelte-ignore a11y_no_noninteractive_tabindex -->
<div
  class="tp-card group relative aspect-[59/86] select-none transition-transform duration-150"
  class:cursor-pointer={interactive}
  role={interactive ? "button" : undefined}
  tabindex={interactive ? 0 : undefined}
  aria-pressed={action === "target" ? picked : undefined}
  aria-label={accessibleName}
  onclick={interactive ? onclick : undefined}
  onkeydown={interactive ? (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onclick?.()) : undefined}
  onmouseenter={() => onhover?.(true)}
  onmouseleave={() => onhover?.(false)}
>
  {#if !card}
    <!-- Empty zone slot (.tp-zone). Always visible so the board's zones read clearly. Default: a glowing
         blue lit zone; over a custom field image (--df-zone-dashed = 1) it fades to a dashed outline so
         printed slots stay visible. A texture pack styles .tp-zone with any CSS (e.g. opacity:0). -->
    <div class="tp-zone zone-empty absolute inset-0 chamfer grid place-items-center" style="--c:4px">
      {#if label}<span class="font-display text-[0.5rem] uppercase tracking-[0.18em] text-cyan/60">{label}</span>{/if}
    </div>
  {:else}
    <div
      class="absolute inset-0 origin-center transition-transform duration-200"
      class:z-10={defense || hinted || linked || picked}
      class:play-halo={hinted && !picked}
      class:picked-halo={picked}
      class:link-halo={linked && !hinted}
      style="{rot ? `transform:rotate(${rot}deg);` : ''}{accent ? `--accent:${accent};` : ''}"
    >
      <!-- cards are plain rounded RECTANGLES (Master-Duel read) — the chamfered octagon shape
           belongs to the zone SOCKETS, never to the card itself -->
      <div
        bind:this={box}
        class="relative size-full overflow-hidden rounded-[5px] ring-1 transition-shadow"
        class:ring-cyan-dim={!hinted && !shownFaceDown}
        class:ring-magenta={!hinted && shownFaceDown}
        class:ring-transparent={hinted}
        class:play-edge={hinted && !picked}
        class:picked-edge={picked}
        class:focus-edge={active && !picked}
      >
        {#if shownFaceDown}
          <!-- Card back: the real classic Yu-Gi-Oh card back art. -->
          {#if backImg}
            <img src={backImg} alt="card back" class="size-full object-cover" draggable="false" />
          {:else}
            <div class="grid size-full place-items-center bg-gradient-to-br from-surface-2 to-void-2">
              <div class="size-7 rotate-45 border border-cyan/50 bg-cyan/10" style="box-shadow:0 0 10px color-mix(in oklab,var(--color-cyan) 40%,transparent)"></div>
            </div>
          {/if}
        {:else}
          <!-- Face-up real card: a foil you own wears its holographic finish here (the wrapper
               carries the foil grade so the sheen sits under the badges below). -->
          <div class="size-full {foilGradeClass(foil)}">
            {#if !broken}
              <img src={cardImage(card.code)} alt={info?.name ?? `Card ${card.code}`} onerror={() => (broken = true)} class="size-full object-cover" draggable="false" />
            {:else}
              <!-- No CDN art: a coloured frame with the name + stats. -->
              <div class="flex size-full flex-col" style="background:{info ? frameColor(info) : '#1a2742'}">
                <div class="truncate px-1 pt-1 text-center text-[0.5rem] font-bold leading-tight text-white/95" style="text-shadow:0 1px 1px rgba(0,0,0,.6)">{info?.name ?? `#${card.code}`}</div>
                <div class="m-1 flex-1 bg-black/25"></div>
                {#if isMonster}
                  <div class="px-1 pb-1 text-right text-[0.5rem] font-bold tabular-nums text-white/95" style="text-shadow:0 1px 1px rgba(0,0,0,.6)">{fmt(card.attack)}/{fmt(card.defense)}</div>
                {/if}
              </div>
            {/if}
          </div>
        {/if}

        <!-- Xyz material count. -->
        {#if card.overlayCount}
          <span class="absolute left-0.5 top-0.5 grid size-3.5 place-items-center rounded-full bg-void/85 font-display text-[0.55rem] font-bold text-gold">{card.overlayCount}</span>
        {/if}

      </div>
      {#if action === "target" && !picked}
        <div class="target-corners pointer-events-none absolute -inset-1" aria-hidden="true"></div>
      {/if}
    </div>

    {#if picked}
      <span class="picked-confirm pointer-events-none absolute left-1/2 top-1/2 z-40 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1 rounded-sm px-1.5 py-1 font-display text-[0.48rem] font-extrabold uppercase tracking-[0.1em] text-void" aria-hidden="true">
        <svg viewBox="0 0 16 16" class="size-2.5" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 8 3 3 7-7" /></svg>
        Selected
      </span>
    {/if}

    <!-- (ATK/DEF + level labels moved to DuelStatLabels.svelte: a SCREEN-SPACE overlay outside the
         perspective plane — in-card labels inherited the 3D projection and depth-sorting.) -->

    <!-- Counter badge, upright (outside the rotation wrapper), top-right so it never clashes with the
         top-left Xyz-material count. Gold pip = "this card holds counters (Spell Counters / A-Counters…)". -->
    {#if counterTotal > 0}
      <span class="counter-chip pointer-events-none absolute right-0.5 top-0.5 z-20 grid h-[0.95rem] min-w-[0.95rem] place-items-center rounded-sm bg-void/85 px-0.5 font-display text-[0.55rem] font-bold leading-none tabular-nums text-gold">◆{counterTotal}</span>
    {/if}

    <!-- Equip / target relationship badge, shown on this card AND every card it's linked to while one
         of them is hovered (an equip and its host, a targeter and its target(s)). Bottom-left, upright. -->
    {#if linked}
      <span class="link-chip pointer-events-none absolute bottom-0.5 left-0.5 z-30 grid size-[0.95rem] place-items-center rounded-sm bg-void/85 text-gold">
        <svg viewBox="0 0 24 24" class="size-2.5" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
          <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
        </svg>
      </span>
    {/if}
  {/if}
</div>

<style>
  /* Empty-zone slot. --df-zone-dashed (0 default, 1 when a theme sets a field image) cross-fades from
     the lit-blue zone to a neutral dashed outline so printed slots on the field art stay legible.
     `chamfer` clips the corners; the dashed border follows the clipped edges. */
  .zone-empty {
    --d: var(--df-zone-dashed, 0);
    background: color-mix(in oklab, var(--color-cyan) calc(7% * (1 - var(--d))), transparent);
    box-shadow:
      inset 0 0 0 1px color-mix(in oklab, var(--color-cyan) calc(35% * (1 - var(--d))), transparent),
      inset 0 0 calc(14px * (1 - var(--d))) color-mix(in oklab, var(--color-cyan) 22%, transparent);
    border: 1.5px dashed color-mix(in oklab, var(--color-ink) calc(60% * var(--d)), transparent);
  }

  /* "You can act here" highlight (Master-Duel style): ONE warm-gold glow on every actionable card.
     Two layers because the card is chamfered (clip-path) and a clip-path eats OUTER shadows, the
     crisp gold edge is an INSET shadow on the clipped card; the soft halo is a drop-shadow on the
     UNCLIPPED wrapper so it can bloom past the edge. Colour comes from the inline `--accent` (gold). */
  .play-edge {
    box-shadow:
      inset 0 0 0 1.5px var(--accent),
      inset 0 0 6px color-mix(in oklab, var(--accent) 38%, transparent);
  }
  /* Legal targets use four precise lock-on corners rather than another generic full-card glow. */
  .target-corners {
    background:
      linear-gradient(var(--accent), var(--accent)) left top / 30% 2px no-repeat,
      linear-gradient(var(--accent), var(--accent)) left top / 2px 20% no-repeat,
      linear-gradient(var(--accent), var(--accent)) right top / 30% 2px no-repeat,
      linear-gradient(var(--accent), var(--accent)) right top / 2px 20% no-repeat,
      linear-gradient(var(--accent), var(--accent)) left bottom / 30% 2px no-repeat,
      linear-gradient(var(--accent), var(--accent)) left bottom / 2px 20% no-repeat,
      linear-gradient(var(--accent), var(--accent)) right bottom / 30% 2px no-repeat,
      linear-gradient(var(--accent), var(--accent)) right bottom / 2px 20% no-repeat;
    filter: drop-shadow(0 0 3px var(--accent));
    animation: target-corners-breathe 1.25s ease-in-out infinite;
  }
  @keyframes target-corners-breathe {
    0%, 100% { opacity: 0.62; scale: 0.96; }
    50% { opacity: 1; scale: 1.025; }
  }
  /* A chosen card stops breathing and locks into a decisive lime confirmation state. The centred
     chip stays upright even when the physical card is in Defense Position or across the table. */
  .picked-edge {
    box-shadow:
      inset 0 0 0 2px var(--color-lime),
      inset 0 0 12px color-mix(in oklab, var(--color-lime) 48%, transparent);
  }
  .picked-halo {
    filter: drop-shadow(0 0 3px var(--color-lime)) drop-shadow(0 0 8px color-mix(in oklab, var(--color-lime) 70%, transparent));
  }
  .picked-confirm {
    background: var(--color-lime);
    box-shadow: 0 0 0 1px rgba(3, 10, 14, 0.9), 0 0 12px color-mix(in oklab, var(--color-lime) 65%, transparent);
    animation: picked-confirm-in 180ms var(--ease-out-quint, cubic-bezier(0.22, 1, 0.36, 1)) both;
  }
  @keyframes picked-confirm-in {
    from { opacity: 0; transform: translate(-50%, -50%) scale(0.72); }
    to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  }
  .focus-edge { outline: 2px solid var(--color-gold); }
  /* Thin, concentrated, bright glow. The colour stays CONSTANT (var(--accent)); only the blur radius
     breathes, so it interpolates smoothly instead of stepping between colours. */
  .play-halo {
    animation: halo-breathe 1.6s ease-in-out infinite;
    will-change: filter;
  }
  @keyframes halo-breathe {
    0%, 100% { filter: drop-shadow(0 0 2px var(--accent)); }
    50%      { filter: drop-shadow(0 0 5px var(--accent)); }
  }
  /* Hovering an actionable card, including a valid TARGET during a pick, pops its glow brighter so
     the specific candidate under the cursor stands out from the others that merely glow as pickable. */
  .group:hover .play-edge {
    box-shadow:
      inset 0 0 0 2px var(--accent),
      inset 0 0 10px color-mix(in oklab, var(--accent) 55%, transparent);
  }
  .group:hover .play-halo {
    animation: none;
    filter: drop-shadow(0 0 7px var(--accent)) drop-shadow(0 0 3px var(--accent));
  }

  /* Counter badge: a gold pip that catches the eye without shouting, thin gold ring + soft glow. */
  .counter-chip {
    box-shadow: 0 0 0 1px color-mix(in oklab, var(--color-gold) 55%, transparent),
                0 0 8px color-mix(in oklab, var(--color-gold) 30%, transparent);
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.85);
  }

  /* Equip/target link badge, a gold chain-link chip, ringed + glowing so the connection reads clearly. */
  .link-chip {
    box-shadow: 0 0 0 1px color-mix(in oklab, var(--color-gold) 60%, transparent),
                0 0 9px color-mix(in oklab, var(--color-gold) 38%, transparent);
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.8));
  }
  /* A gentle gold halo on linked cards (when not already wearing the orange play-halo) so the related
     cards stand out at a glance, not just the badge. */
  .link-halo {
    animation: link-breathe 1.5s ease-in-out infinite;
    will-change: filter;
  }
  @keyframes link-breathe {
    0%, 100% { filter: drop-shadow(0 0 2px var(--color-gold)); }
    50%      { filter: drop-shadow(0 0 6px var(--color-gold)); }
  }

  @media (prefers-reduced-motion: reduce) {
    .play-halo { animation: none; filter: drop-shadow(0 0 4px var(--accent)); }
    .link-halo { animation: none; filter: drop-shadow(0 0 4px var(--color-gold)); }
    .target-corners, .picked-confirm { animation: none; }
  }
</style>
